/** @jsxImportSource @compiled/react */
import { useCallback, useEffect, useState } from "react";

import { useIntl } from "react-intl";

import { ConfluenceIcon } from "@atlaskit/logo-next";

import { experiments, useExperiment } from "@/utils/experiment";
import { getLocalStorage, setLocalStorage } from "@/utils/localstorage";

import { SmartBanner } from "./smart-banner";

const STORAGE_KEY = "SmartBanner_dismiss-confluence";
const DISMISS_EXPIRY = 24 * 60 * 60;

// iOS deep link and App Store URL
const IOS_DEEP_LINK = "confluence://home";
const IOS_APP_STORE_URL =
    "https://apps.apple.com/app/confluence-cloud/id1006971684";

// Android Intent URL with built-in Play Store fallback
const ANDROID_PLAY_STORE_URL =
    "https://play.google.com/store/apps/details?id=com.atlassian.android.confluence.core";
const ANDROID_INTENT_URL = `intent://home#Intent;scheme=confluence;package=com.atlassian.android.confluence.core;S.browser_fallback_url=${encodeURIComponent(
    ANDROID_PLAY_STORE_URL
)};end`;

// Timeout for iOS fallback (Android Intent handles fallback automatically)
const IOS_DEEP_LINK_TIMEOUT = 1000;

type MobileDeviceInfo = {
    isMobile: boolean;
    isIOS: boolean;
    isAndroid: boolean;
};

const useMobileDeviceInfo = (): MobileDeviceInfo => {
    const [deviceInfo, setDeviceInfo] = useState<MobileDeviceInfo>({
        isMobile: false,
        isIOS: false,
        isAndroid: false
    });

    useEffect(() => {
        if (typeof window !== "undefined" && window.navigator) {
            const userAgent = window.navigator.userAgent;
            const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
            const isAndroid = /Android/i.test(userAgent);
            setDeviceInfo({
                isMobile: isIOS || isAndroid,
                isIOS,
                isAndroid
            });
        }
    }, []);

    return deviceInfo;
};

export interface ConfluenceSmartBannerProps {
    className?: string;
}

/**
 * ConfluenceSmartBanner - iOS/Android smart app banner for Confluence
 *
 * This component:
 * - Only renders on iOS and Android devices
 * - Only renders on /software/confluence and /software/confluence/* paths
 * - Is controlled by the gwp9508ConfluenceBannerIosNativeApp experiment
 * - Persists dismiss state in localStorage for 24 hours
 * - Attempts to open the Confluence app via deep link, falls back to app store
 */
export const ConfluenceSmartBanner = ({
    className
}: ConfluenceSmartBannerProps) => {
    const { isMobile, isAndroid } = useMobileDeviceInfo();
    const [isDismissed, setIsDismissed] = useState(true);
    const intl = useIntl();

    const experimentStatus = useExperiment(
        experiments.gwp9508ConfluenceBannerIosNativeApp
    );

    const isExperimentEnabled =
        experimentStatus.type === "enrolled" && experimentStatus.value === true;

    useEffect(() => {
        const dismissValue = getLocalStorage(STORAGE_KEY, true);
        if (dismissValue !== "1") {
            setIsDismissed(false);
        }
    }, []);

    const handleClose = () => {
        setIsDismissed(true);
        setLocalStorage(STORAGE_KEY, "1", DISMISS_EXPIRY);
    };

    // Get the appropriate deep link URL based on platform
    // Android uses Intent URL with built-in fallback, iOS uses simple deep link
    const getDeepLinkUrl = useCallback(() => {
        if (isAndroid) {
            return ANDROID_INTENT_URL;
        }
        return IOS_DEEP_LINK;
    }, [isAndroid]);

    const handleButtonClick = useCallback(() => {
        if (isAndroid) {
            return;
        }

        // iOS: Set up timeout-based fallback to App Store
        let didNavigateAway = false;

        const handleVisibilityChange = () => {
            if (document.hidden) {
                didNavigateAway = true;
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        setTimeout(() => {
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );

            // Only redirect to App Store if user didn't navigate away (app didn't open)
            if (!didNavigateAway && !document.hidden) {
                window.location.href = IOS_APP_STORE_URL;
            }
        }, IOS_DEEP_LINK_TIMEOUT);
    }, [isAndroid]);

    if (!isMobile || !isExperimentEnabled || isDismissed) {
        return null;
    }

    return (
        <SmartBanner
            appIcon={
                <ConfluenceIcon
                    appearance="brand"
                    size="medium"
                    shouldUseNewLogoDesign
                />
            }
            title={intl.formatMessage({
                id: "ConfluenceSmartBanner.title",
                defaultMessage: "Get the Confluence app"
            })}
            subtitle={intl.formatMessage({
                id: "ConfluenceSmartBanner.subtitle",
                defaultMessage: "Collaborate on the go"
            })}
            buttonText={intl.formatMessage({
                id: "ConfluenceSmartBanner.buttonText",
                defaultMessage: "OPEN"
            })}
            buttonHref={getDeepLinkUrl()}
            onButtonClick={handleButtonClick}
            onClose={handleClose}
            className={className}
            dataEventProps={{
                "data-product-key": "confluence",
                "data-event-container": "smartBanner",
                "data-label": "OPEN",
                "data-label-english": "OPEN"
            }}
        />
    );
};

export default ConfluenceSmartBanner;
